import { Link, useParams, Navigate } from 'react-router-dom';
import { Play, Plus, Check, Send } from 'lucide-react';
import { SmartImage } from '@/shared/ui/SmartImage';
import { getMovieById, getMoviesByIds } from '@/shared/data';
import { useStore } from '@/shared/state/store';
import { MovieCard } from './components/MovieCard';
import { EpisodeRow } from './components/EpisodeRow';
import { cn } from '@/shared/utils/cn';

export function MovieDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const movie = getMovieById(id);
  const inList = useStore((s) => s.myListMovieIds.has(id));
  const toggleList = useStore((s) => s.toggleMyList);

  if (!movie) return <Navigate to="/movieflix" replace />;
  const similar = getMoviesByIds(movie.similarIds);

  return (
    <div data-testid="movie-detail" data-movie-id={movie.id} className="-mt-16 pb-16">
      {/* Detail hero uses the same blurred-poster-bg + centered-poster
          treatment as the home billboard, so this page feels like an
          extension of the row card the user just tapped. Single image source
          (movie.poster) so colors stay consistent. */}
      <div className="relative w-full h-[55vh] overflow-hidden">
        <SmartImage
          fill
          src={movie.poster}
          alt=""
          sizes="100vw"
          className="h-full"
          style={{
            filter: 'blur(40px) brightness(0.45) saturate(1.1)',
            transform: 'scale(1.2)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-velvet/40 via-velvet/20 to-velvet" />
        <div className="absolute inset-x-0 top-0 flex items-center justify-center pt-20 pointer-events-none">
          <div className="relative w-[40%] max-w-[200px] aspect-[2/3] rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)]">
            <SmartImage fill src={movie.poster} alt={movie.title} sizes="220px" className="h-full" />
          </div>
        </div>
      </div>
      <div className="-mt-24 px-4 relative z-10">
        <h1 className="font-serif italic text-3xl text-white tracking-wide">{movie.title}</h1>
        <div className="mt-1 flex items-center gap-2 text-[11px] text-white/60 uppercase tracking-wider">
          <span>{movie.year}</span>
          <span>·</span>
          <span>{movie.rated}</span>
          <span>·</span>
          <span>
            {movie.isSeries
              ? `${movie.seasons} season${(movie.seasons ?? 1) > 1 ? 's' : ''}`
              : `${movie.runtimeMin}m`}
          </span>
        </div>
        <p className="mt-3 text-white/75 text-sm leading-relaxed">{movie.synopsis}</p>

        <div className="mt-5 flex items-center gap-3">
          <Link
            data-testid="detail-play"
            to={`/movieflix/watch/${movie.id}`}
            className="flex-1 flex items-center justify-center gap-2 bg-white text-velvet px-4 py-3 rounded-xl font-medium text-sm active:scale-95"
          >
            <Play className="w-4 h-4 fill-current" /> Play
          </Link>
          <button
            data-testid="detail-add-list"
            data-in-list={inList}
            onClick={() => toggleList(movie.id)}
            className={cn(
              'w-12 h-12 rounded-xl glass flex items-center justify-center active:scale-95',
              inList && 'border-amber-200/40',
            )}
            aria-label="My List"
          >
            {inList ? <Check className="w-4 h-4 text-amber-200" /> : <Plus className="w-4 h-4 text-white" />}
          </button>
          <button
            className="w-12 h-12 rounded-xl glass flex items-center justify-center active:scale-95"
            aria-label="Share"
          >
            <Send className="w-4 h-4 text-white" />
          </button>
        </div>

        {movie.cast.length > 0 && (
          <section className="mt-7">
            <h3 className="font-serif italic text-base text-white mb-2">Cast</h3>
            <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2">
              {movie.cast.map((c) => (
                <div key={c.name} className="flex-shrink-0 w-20 text-center">
                  <div className="relative w-16 h-16 mx-auto rounded-full overflow-hidden bg-black/30">
                    <SmartImage fill src={c.avatar} alt={c.name} sizes="64px" className="h-full" />
                  </div>
                  <div className="text-[11px] text-white/80 mt-1.5 truncate">{c.name}</div>
                  <div className="text-[10px] text-white/40 truncate">{c.role}</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {movie.isSeries && movie.episodes && <EpisodeRow episodes={movie.episodes} />}

      {similar.length > 0 && (
        <section className="mt-8 pl-4">
          <h3 className="font-serif italic text-lg text-white mb-3 pr-4">More like this</h3>
          <div className="flex gap-3 overflow-x-auto pb-3 pr-4 scrollbar-hide snap-x">
            {similar.map((m) => (
              <MovieCard key={m.id} movie={m} variant="portrait" />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
